import { useEffect, useState } from "react";
import { usePatientAuth } from "../../store/authPatientStore";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import StatusBadge from "../../components/identity-verification/StatusBadge";
import DocumentUploadField from "../../components/identity-verification/DocumentUploadField";
import {
  getPatientIdentityVerification,
  submitPatientIdentityVerification,
  type PatientIdentityVerification,
} from "../../api/identityVerification";

export default function PatientIdentityVerificationPage() {
  const { patient } = usePatientAuth();
  const nip = patient?.nip?.trim() ?? "";
  const [state, setState] = useState<PatientIdentityVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nip) return;
    let mounted = true;
    setLoading(true);
    getPatientIdentityVerification(nip).then((res) => {
      if (!mounted) return;
      if (res.ok) setState(res.data);
      else setError(res.message);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [nip]);

  const handleSubmit = async (carteCipUrl: string) => {
    if (!nip) return { ok: false as const, message: "Session invalide." };
    const res = await submitPatientIdentityVerification(nip, { carteCipUrl });
    if (!res.ok) return { ok: false as const, message: res.message };
    setState(res.data);
    return { ok: true as const };
  };

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Vérification d'identité"
        description="Soumettez votre carte CIP pour faire vérifier votre compte."
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Carte CIP</CardTitle>
          {state && <StatusBadge status={state.statut} />}
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && !loading && <p className="text-sm text-red-600">{error}</p>}

          {!loading && state && (
            <div className="space-y-4">
              {state.statut === "rejetee" && state.motifRejet && (
                <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <span className="font-semibold">Motif du rejet : </span>
                  {state.motifRejet}
                </div>
              )}
              <DocumentUploadField
                label="Carte CIP"
                folderName="identity-verification/patients"
                filename={nip ? `carte_cip_${nip}` : undefined}
                currentUrl={state.carteCipUrl}
                readOnly={state.statut === "validee"}
                onSubmitted={handleSubmit}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
